import {
  Modal,
  Button,
  Pill,
  Loader,
  styled,
} from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { usePermissions } from "@/hooks";

import {
  assignContactsToCase,
  describeAssignOutcome,
  settledContacts,
} from "../contacts.assign";
import { Contact } from "../contacts.types";
import { ContactsList, ContactsListHandle } from "../Lists";

interface AssignContactsToCaseModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (contacts: Contact[]) => void;
  /** Defaults to `onClose` — the two are the same gesture unless a caller says so. */
  onCancel?: () => void;
  buttonProps?: Partial<{
    cancelText: string;
    submitText: string;
  }>;
  caseId: number;
}

/**
 * Definite height, capped to the viewport exactly as Modal's own max-height is.
 * Everything inside depends on it: the list sizes against a bounded parent, and
 * with the total pinned and every other block in the column a fixed size, what
 * the list gets is a constant — no part of the modal moves as the selection
 * grows. An auto height would instead put the pills row's growth into the
 * modal, which on any laptop screen it has nowhere to put.
 *
 * A style object rather than `styled(Modal)`: wrapping would drop `Modal.Title`.
 */
const MODAL_STYLE = { width: 500, height: "min(700px, calc(100vh - 50px))" };

const Intro = styled.div`
  font-weight: 500;
  margin: 20px 0px;
`;

/**
 * The list's share of the modal, and the only part that flexes.
 *
 * Grown rather than sized: List's own container is `height: 0; flex: 1 1 auto`,
 * so content height here is zero and there is nothing to size to. What is left
 * over is a constant, because everything else in this column — the pills block
 * included — is a fixed size that is always in the layout.
 */
const ListArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  /* Where growing into less stops being useful: below a few rows this is no
     longer a picker. A viewport too short to give it this much scrolls Modal's
     own body instead of shrinking the list away. */
  min-height: 200px;
  /* Anchors the submission overlay. */
  position: relative;
`;

/**
 * Covers the list — and the pills, which hide — while the assign request is in
 * flight. The selection is mid-submit, so nothing about it may be editable.
 * Overlays rather than replaces: the list owns the selection, and a failed
 * submit leaves the modal open for a retry that needs it, so the list stays
 * mounted underneath. Absolute keeps the modal from jumping height either way.
 */
const SubmissionStatus = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .status-title {
    font-weight: 600;
  }

  .status-detail {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

/**
 * Hidden with `visibility` rather than unmounted or `display: none`: the list
 * keeps its layout, so its virtualised rows go on measuring against a real
 * height and come back mid-scroll, while dropping out of hit-testing and the
 * tab order for as long as the submit runs.
 */
const ListWrapper = styled.div<{ $hidden: boolean }>`
  display: flex;
  flex-direction: column;
  /* The List's own container is height 0 with flex: 1 1 auto — it only gets
     height by growing into a parent that has one. */
  flex: 1 1 auto;
  min-height: 0;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
`;

/**
 * Selected contacts read back as pills so a selection that scrolled out of the
 * list — or off it entirely, once a search narrows the rows — stays visible and
 * removable.
 *
 * Every part of this block is a fixed size, and it is always in the layout: the
 * modal is pinned, so anything that varies here would come out of the list
 * above it, and a list that resizes as you tick rows through it is the thing
 * this is meant to help with.
 */
const SelectedContacts = styled.div<{ $hidden: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: 6px;
  margin: 12px 0;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};

  .selected-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .selected-label {
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .selected-pills {
    display: flex;
    flex-wrap: wrap;
    /* Three rows of xs pills (20px each) plus their 4px gaps. Definite rather
       than a max: a max grows row by row as pills wrap, and every pixel it
       grows by is a pixel off the list. A fourth row scrolls instead. */
    height: 68px;
    /* Rows pack against the top rather than stretching down a box that is now
       taller than its content. */
    align-content: flex-start;
    gap: 4px;
    overflow-y: auto;
  }

  .selected-empty {
    font-size: 11px;
    color: ${({ theme }) => theme.palette.text.disabled};
  }
`;

/**
 * ButtonMenu carries `flex: 1 1 auto` for laying out its own row. In this column
 * that grow would make it split the leftover height with the list, so it is
 * pinned to its content here.
 *
 * Inline rather than `styled(ButtonMenu)`: monolith-ui bundles its own copy of
 * styled-components, so its `styled` and the one ButtonMenu is built with are
 * separate instances writing to separate stylesheets. Which of the two `flex`
 * rules lands last would be up to injection order; an inline style is not.
 */
const ACTIONS_STYLE = { flex: "0 0 auto" };

const SelectedContactsPanel: React.FC<{
  contacts: Contact[];
  hidden: boolean;
  onClear: () => void;
  onRemove: (clientId: number) => void;
}> = ({ contacts, hidden, onClear, onRemove }) => (
  <SelectedContacts $hidden={hidden}>
    <div className="selected-header">
      <div className="selected-label">{contacts.length} Selected</div>
      {/*
        Always rendered and always live. It is the tallest thing in this row, so
        rendering it conditionally would hand the list back the button's height
        on every first tick — and the list holds selection state this row cannot
        show (exclusions standing in for rows that scrolled away), so a count of
        zero pills is not proof there is nothing to clear. Clearing an empty
        selection is a no-op; being unable to clear a stuck one is not.
      */}
      <Button
        size="xs"
        variant="text"
        color="red"
        leftSection={<XIcon size={12} />}
        onClick={onClear}
      >
        Clear
      </Button>
    </div>
    <div className="selected-pills">
      {contacts.length === 0 ? (
        <div className="selected-empty">
          No contacts selected yet — tick rows above.
        </div>
      ) : (
        contacts.map((contact) => (
          <Pill
            key={contact.client_id}
            size="xs"
            title={contact.name}
            onRemove={() => onRemove(contact.client_id)}
          >
            {contact.name}
          </Pill>
        ))
      )}
    </div>
  </SelectedContacts>
);

export const AssignContactsToCaseModal: React.FC<AssignContactsToCaseModalProps> =
  ({ show, onClose, onSubmit, onCancel, buttonProps, caseId }) => {
    const [submitting, setSubmitting] = useState(false);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [processedCount, setProcessedCount] = useState(0);
    const [submitTotal, setSubmitTotal] = useState(0);
    const listRef = useRef<ContactsListHandle>(null);
    const { currentUser } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();

    /*
      This modal outlives a single opening while the list inside it does not —
      Modal only renders its children while open. Without this reset a selection
      would survive a close, showing pills the remounted list knows nothing
      about and submitting them on the next Assign.
    */
    useEffect(() => {
      if (!show) {
        setContacts([]);
        setProcessedCount(0);
        setSubmitTotal(0);
      }
    }, [show]);

    const handleSubmit = async () => {
      // The list stays mounted through the submit, so snapshot the selection
      // rather than let a late change shift the tally out from under it.
      const selected = contacts;

      setSubmitting(true);
      setProcessedCount(0);
      setSubmitTotal(selected.length);

      try {
        const outcome = await assignContactsToCase({
          caseId,
          contacts: selected,
          userId: currentUser?.user_id,
          onProgress: () => setProcessedCount((count) => count + 1),
        });

        // Whatever reached the case still has to reach the case's contact list.
        const settled = settledContacts(outcome);
        if (settled.length > 0) onSubmit?.(settled);

        const { message, variant } = describeAssignOutcome(outcome);
        enqueueSnackbar(message, { variant });

        if (outcome.failed.length === 0) {
          onClose?.();
          return;
        }

        outcome.failed.forEach(({ contact, reason }) =>
          console.error(
            `Error linking contact ${contact.name} to case:`,
            reason
          )
        );

        /*
          Anything short of a clean run leaves the modal open: closing would
          throw away the selection the retry needs. What did reach the case is
          unticked so that retry links only the contacts that failed.
        */
        settled.forEach((contact) =>
          listRef.current?.deselect(contact.client_id)
        );
      } catch (error) {
        /*
          assignContactsToCase settles every contact itself, so getting here
          means something outside them broke and nothing is known about what
          linked — stay open, and leave the selection alone.
        */
        console.error("Error linking contacts to case:", error);
        enqueueSnackbar(`Error linking contacts to case.`, {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
      }
    };

    // A backdrop click or Escape would otherwise walk away from an in-flight
    // request. handleSubmit closes through `onClose` directly, so it is unaffected.
    const handleDismiss = () => {
      if (submitting) return;
      onClose?.();
    };

    return (
      <Modal open={show} onClose={handleDismiss} style={MODAL_STYLE}>
        <Modal.Title>Assign Contacts To Case</Modal.Title>
        <Intro>
          Select contacts from the list below to assign them to this case.
        </Intro>
        <ListArea>
          {submitting && (
            <SubmissionStatus>
              <Loader size={22} />
              <div className="status-title">Assigning Contacts…</div>
              <div className="status-detail">
                {processedCount} of {submitTotal} processed
              </div>
            </SubmissionStatus>
          )}
          <ListWrapper $hidden={submitting}>
            {/*
              assigned_case_id, not case_id: case_id INNER JOINs contact_assign
              and narrows the list *to* the case's contacts, which is the exact
              complement of what a picker needs. This leaves the set whole and
              flags each record instead, so contacts already on the case show as
              unpickable rather than silently linking to nothing.
            */}
            <ContactsList
              ref={listRef}
              query={{ assigned_case_id: caseId }}
              onSelectionChange={(e) => {
                setContacts(e.contacts);
              }}
            />
          </ListWrapper>
        </ListArea>
        <SelectedContactsPanel
          contacts={contacts}
          hidden={submitting}
          onClear={() => listRef.current?.clearSelection()}
          onRemove={(clientId) => listRef.current?.deselect(clientId)}
        />
        <ButtonMenu style={ACTIONS_STYLE}>
          <Button
            size="xs"
            variant="subtle"
            onClick={onCancel ?? onClose}
            disabled={submitting}
          >
            {buttonProps?.cancelText || "Cancel"}
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            loading={submitting}
            disabled={contacts.length === 0 || submitting}
          >
            {buttonProps?.submitText || `Assign ${contacts.length} Contact(s)`}
          </Button>
        </ButtonMenu>
      </Modal>
    );
  };
